import type { RefObject } from 'react';
import { useLayoutEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { DEFAULT_OVERSCAN } from './constants';
import type { VirtualAxisConfigT } from './types';
type VirtualGridParamsT = {
  row: VirtualAxisConfigT;
  column: VirtualAxisConfigT;
  scrollRef: RefObject<HTMLDivElement | null>;
};
type AxisOptionsArgsT = {
  axis: VirtualAxisConfigT;
  horizontal: boolean;
  /** Sticky thickness preceding this axis on scroll (the cross-axis header). */
  leadingSize: number;
  scrollRef: RefObject<HTMLDivElement | null>;
};
const getAxisOptions = ({
  axis,
  horizontal,
  leadingSize,
  scrollRef
}: AxisOptionsArgsT) => ({
  horizontal,
  count: axis.count,
  getScrollElement: () => scrollRef.current,
  estimateSize: () => axis.itemSize,
  overscan: axis.overscan ?? DEFAULT_OVERSCAN,
  scrollMargin: leadingSize,
  scrollPaddingStart: leadingSize,
  // scrollToIndex must clear the sticky cross-axis header
  paddingStart: axis.paddingStart,
  paddingEnd: axis.paddingEnd
});

/**
 * Row + column virtualizers sharing one scroll element. Each axis's `scrollMargin`
 * is the cross-axis header thickness, so position items by `start - scrollMargin`.
 */
export function useVirtualGrid({
  row,
  column,
  scrollRef
}: VirtualGridParamsT) {
  const rowVirtualizer = useVirtualizer(getAxisOptions({
    axis: row,
    horizontal: false,
    leadingSize: column.headerSize,
    scrollRef
  }));
  const columnVirtualizer = useVirtualizer(getAxisOptions({
    axis: column,
    horizontal: true,
    leadingSize: row.headerSize,
    scrollRef
  }));

  /* TanStack Virtual caches measured item sizes and only re-derives them when count/padding/
   scrollMargin/getItemKey change — not when `itemSize` (estimateSize's source value) changes.
   Force a remeasure so a dynamic itemSize (e.g. timeline zoom) actually resizes cells.
   https://github.com/TanStack/virtual/discussions/379
   */
  useLayoutEffect(() => {
    rowVirtualizer.measure();
  }, [row.itemSize, rowVirtualizer]);
  useLayoutEffect(() => {
    columnVirtualizer.measure();
  }, [column.itemSize, columnVirtualizer]);
  return {
    rowVirtualizer,
    columnVirtualizer
  };
}